import { jsPDF } from "jspdf";
import moment from "moment";
import React, { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import {
  Card,
  CardBody,
  Col,
  Container,
  Nav,
  NavItem,
  NavLink,
  Row,
  TabContent,
  Table,
  TabPane,
} from "reactstrap";
import Swal from "sweetalert2";
import classnames from "classnames";
import ButtonBack from "../../Components/ButtonBack";
import HeadersRole from "../../Components/Headers/HeadersRole";
import reportsIcon from "../../assets/images/iconsRoles/IndividualsIcon.svg";
import { useCurrentUser } from "../../slices/auth/hooks";
import { getReportById } from "../../slices/general/thunk";

const ReportDetails = () => {
  const { id } = useParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const currentUser = useCurrentUser();

  const [loading, setLoading] = useState(false);
  const [report, setReport] = useState(null);
  const [activeTab, setActiveTab] = useState("active");

  const toggleTab = (tab) => {
    if (activeTab !== tab) {
      setActiveTab(tab);
    }
  };

  const downloadReport = () => {
    const doc = new jsPDF();
    let currentY = 20;

    const date = report.effectiveForDate;
    // See note in DashboardReports.js — effectiveForDate is the next
    // coverage ED, but the report semantically covers the work month
    // (two months earlier).
    const reportMonth = moment.utc(date).subtract(2, 'month').format("MMMM YYYY");
    const createdAt = moment.utc(date).format("MM/DD/YYYY");

    // Header
    doc.setFontSize(20);
    doc.setTextColor(0, 0, 0);
    doc.text("Employee Report", 20, currentY);
    currentY += 15;

    // Report info
    doc.setFontSize(12);
    doc.setTextColor(80, 80, 80);
    doc.text(`Month: ${reportMonth}`, 20, currentY);
    currentY += 7;
    doc.text(`Created On: ${createdAt}`, 20, currentY);
    currentY += 7;
    doc.text(`Active Employees: ${report.summary?.totalActive || 0}`, 20, currentY);
    currentY += 7;
    doc.text(`Terminated Employees: ${report.summary?.totalTerminated || 0}`, 20, currentY);
    currentY += 15;

    const lineHeight = 8;
    const pageHeight = doc.internal.pageSize.height - 20;

    const addSection = (title, employees, startY, showTerminatedOn = false) => {
      let y = startY;

      if (employees?.length > 0) {
        doc.setFontSize(14);
        doc.setTextColor(0, 0, 0);
        doc.setFont(undefined, "bold");
        doc.text(title, 20, y);
        y += 10;

        // Table header
        doc.setFontSize(11);
        doc.text("#", 20, y);
        doc.text("Employee Name", 35, y);
        doc.text("Tier", 120, y);
        if (showTerminatedOn) {
          doc.text("Terminated On", 150, y);
        }
        y += 3;

        doc.setDrawColor(200, 200, 200);
        doc.line(20, y, 190, y);
        y += 7;

        doc.setFont(undefined, "normal");

        employees.forEach((employee, index) => {
          if (y + lineHeight > pageHeight) {
            doc.addPage();
            y = 20;

            // Re-add header on new page
            doc.setFont(undefined, "bold");
            doc.text("#", 20, y);
            doc.text("Employee Name", 35, y);
            doc.text("Tier", 120, y);
            if (showTerminatedOn) {
              doc.text("Terminated On", 150, y);
            }
            y += 3;
            doc.line(20, y, 190, y);
            y += 7;
            doc.setFont(undefined, "normal");
          }

          const tierLabel = employee.tier || "N/A";
          const name = `${employee.firstName || ""} ${employee.lastName || ""}`.trim() || "N/A";

          doc.setTextColor(60, 60, 60);
          doc.text(`${index + 1}`, 20, y);
          doc.text(name, 35, y);
          doc.text(tierLabel, 120, y);
          if (showTerminatedOn && employee.terminatedOn) {
            doc.text(moment(employee.terminatedOn).format("MM/DD/YYYY"), 150, y);
          }
          y += lineHeight;
        });

        y += 10;
      }

      return y;
    };

    currentY = addSection("Active Employees", report.activeEmployees, currentY, false);
    currentY = addSection("Terminated Employees", report.terminatedEmployees, currentY, true);

    const fileName = `BaxRx - Report - ${reportMonth}.pdf`;
    doc.save(fileName);
  };

  const fetchReportById = async () => {
    try {
      setLoading(true);
      const actionResult = await dispatch(
        getReportById({
          userId: currentUser.id,
          reportId: id,
        })
      );
      const res = actionResult.payload;

      if (res.error) {
        Swal.fire({
          title: "Error",
          text: res.error,
          icon: "error",
        });
        navigate("/reports");
      } else {
        setReport(res);
      }
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchReportById();
  }, []);

  if (loading || !report) {
    return (
      <div className="page-content">
        <div
          className="text-center d-flex justify-content-center align-items-center"
          style={{ minHeight: "150px", height: "150px" }}
        >
          <div className="spinner-border spinner-border-lg mr-3"></div>
        </div>
      </div>
    );
  }

  // Same correction as above — display the work month, not the next ED.
  const reportMonth = moment.utc(report.effectiveForDate).subtract(2, 'month').format("MMMM YYYY");
  const createdAt = moment.utc(report.effectiveForDate).format("MM/DD/YYYY");

  const renderActiveEmployeesTable = (employees) => (
    <div className="table-responsive">
      <Table className="table table-hover border table-centered align-middle table-nowrap mb-0">
        <thead className="bg" style={{ background: "var(--thead-bg)" }}>
          <tr>
            <th scope="col" style={{ width: "60px" }}>#</th>
            <th scope="col">Employee Name</th>
            <th scope="col">Email</th>
            <th scope="col" className="text-center" style={{ width: "120px" }}>Tier</th>
          </tr>
        </thead>
        <tbody>
          {!employees || employees.length === 0 ? (
            <tr>
              <td colSpan="4" className="text-center">
                No active employees
              </td>
            </tr>
          ) : (
            employees.map((employee, index) => {
              const name = `${employee.firstName || ""} ${employee.lastName || ""}`.trim() || "N/A";
              const tier = employee.tier || "N/A";
              const isFamilyTier = tier === "F" || tier.toLowerCase().includes("family") || tier.toLowerCase().includes("spouse") || tier.toLowerCase().includes("child") || tier.toLowerCase().includes("dependent");

              return (
                <tr key={employee.employeeId || index}>
                  <td>{index + 1}</td>
                  <td>{name}</td>
                  <td>{employee.email || "N/A"}</td>
                  <td className="text-center">
                    <span className={`badge rounded-pill ${isFamilyTier ? 'bg-info' : 'bg-secondary'}`}>
                      {tier}
                    </span>
                  </td>
                </tr>
              );
            })
          )}
        </tbody>
      </Table>
    </div>
  );

  const renderTerminatedEmployeesTable = (employees) => (
    <div className="table-responsive">
      <Table className="table table-hover border table-centered align-middle table-nowrap mb-0">
        <thead className="bg" style={{ background: "var(--thead-bg)" }}>
          <tr>
            <th scope="col" style={{ width: "60px" }}>#</th>
            <th scope="col">Employee Name</th>
            <th scope="col">Email</th>
            <th scope="col" className="text-center" style={{ width: "120px" }}>Tier</th>
            <th scope="col" className="text-center" style={{ width: "120px" }}>Terminated On</th>
          </tr>
        </thead>
        <tbody>
          {!employees || employees.length === 0 ? (
            <tr>
              <td colSpan="5" className="text-center">
                No terminated employees this month
              </td>
            </tr>
          ) : (
            employees.map((employee, index) => {
              const name = `${employee.firstName || ""} ${employee.lastName || ""}`.trim() || "N/A";
              const tier = employee.tier || "N/A";
              const isFamilyTier = tier === "F" || tier.toLowerCase().includes("family") || tier.toLowerCase().includes("spouse") || tier.toLowerCase().includes("child") || tier.toLowerCase().includes("dependent");

              return (
                <tr key={employee.employeeId || index}>
                  <td>{index + 1}</td>
                  <td>{name}</td>
                  <td>{employee.email || "N/A"}</td>
                  <td className="text-center">
                    <span className={`badge rounded-pill ${isFamilyTier ? 'bg-info' : 'bg-secondary'}`}>
                      {tier}
                    </span>
                  </td>
                  <td className="text-center">
                    {employee.terminatedOn ? moment(employee.terminatedOn).format("MM/DD/YYYY") : "N/A"}
                  </td>
                </tr>
              );
            })
          )}
        </tbody>
      </Table>
    </div>
  );

  return (
    <div className="page-content px-0">
      <Container fluid>
        <HeadersRole
          role="Report Details"
          icon={reportsIcon}
          svg={true}
          text={
            <span className="fs-15 text-muted fw-normal">
              Employee breakdown for {reportMonth}
            </span>
          }
        />

        <Card className="mb-4">
          <CardBody>
            <div className="d-flex justify-content-between align-items-center mb-4">
              <div>
                <ButtonBack />
              </div>
              <button
                onClick={downloadReport}
                className="btn btn-primary"
              >
                <i className="ri-download-2-line align-bottom me-1"></i>
                Download Report
              </button>
            </div>

            <Row className="mb-4">
              <Col md={3}>
                <div className="border rounded p-3">
                  <p className="text-muted mb-1 small text-uppercase">Month</p>
                  <h5 className="mb-0">{reportMonth}</h5>
                </div>
              </Col>
              <Col md={3}>
                <div className="border rounded p-3">
                  <p className="text-muted mb-1 small text-uppercase">Created On</p>
                  <h5 className="mb-0">{createdAt}</h5>
                </div>
              </Col>
              <Col md={3}>
                <div className="border rounded p-3">
                  <p className="text-muted mb-1 small text-uppercase">Active</p>
                  <h5 className="mb-0 text-success">{report.summary?.totalActive || 0}</h5>
                </div>
              </Col>
              <Col md={3}>
                <div className="border rounded p-3">
                  <p className="text-muted mb-1 small text-uppercase">Terminated</p>
                  <h5 className="mb-0 text-danger">{report.summary?.totalTerminated || 0}</h5>
                </div>
              </Col>
            </Row>

            <Nav tabs className="nav-tabs-custom nav-success mb-3">
              <NavItem>
                <NavLink
                  className={classnames({ active: activeTab === "active" })}
                  onClick={() => toggleTab("active")}
                  style={{ cursor: "pointer" }}
                >
                  <i className="ri-user-follow-line me-1 align-bottom"></i>
                  Active ({report.summary?.totalActive || 0})
                </NavLink>
              </NavItem>
              <NavItem>
                <NavLink
                  className={classnames({ active: activeTab === "terminated" })}
                  onClick={() => toggleTab("terminated")}
                  style={{ cursor: "pointer" }}
                >
                  <i className="ri-user-unfollow-line me-1 align-bottom"></i>
                  Terminated ({report.summary?.totalTerminated || 0})
                </NavLink>
              </NavItem>
            </Nav>

            <TabContent activeTab={activeTab}>
              <TabPane tabId="active">
                {renderActiveEmployeesTable(report.activeEmployees)}
              </TabPane>
              <TabPane tabId="terminated">
                {renderTerminatedEmployeesTable(report.terminatedEmployees)}
              </TabPane>
            </TabContent>
          </CardBody>
        </Card>
      </Container>
    </div>
  );
};

export default ReportDetails;
